import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, ArrowLeft, Image as ImageIcon, Calendar, Tag } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const PublicAdminGalleryDetail = () => {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/admin/galleries/public/${galleryId}`);
        setGallery(response.data);
      } catch (error) {
        toast.error("Failed to load gallery");
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, [galleryId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="bg-card border-border max-w-md">
          <CardContent className="py-16 text-center">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg mb-2">Gallery not found</p>
            <Link to="/galleries" className="text-primary hover:underline">
              Browse other galleries
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const photos = gallery.photos || gallery.assets || [];

  return (
    <div className="min-h-screen py-12 px-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <Link to="/galleries" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Demo Galleries
        </Link>
        
        <Link to="/" className="flex items-center gap-3 mb-6 hover:opacity-80 transition-opacity">
          <Camera className="w-8 h-8 text-primary" />
          <span className="text-2xl font-serif">360Lumina</span>
        </Link>
        
        <h1 className="text-4xl md:text-5xl tracking-tight mb-4">{gallery.title}</h1>
        
        {gallery.description && (
          <p className="text-muted-foreground text-lg mb-4">{gallery.description}</p>
        )}
        
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          {gallery.category && (
            <span className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              {gallery.category}
            </span>
          )}
          {gallery.created_at && (
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(gallery.created_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
          )}
          <span className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            {photos.length} photos
          </span>
        </div>
        
        {gallery.tags && gallery.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {gallery.tags.map((tag, i) => (
              <span key={i} className="px-3 py-1 bg-muted/30 rounded-sm text-sm text-muted-foreground">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Photo Grid */}
      <div className="max-w-7xl mx-auto">
        {photos.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg mb-2">No photos in this gallery yet</p>
              <p className="text-muted-foreground">Check back soon for new photos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <motion.div
                key={photo.asset_id || photo.filename || index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.02 }}
                className="aspect-square relative group cursor-pointer overflow-hidden rounded-sm"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={`${API}/admin/galleries/public/${galleryId}/media/${photo.filename}`}
                  alt={photo.original_name || `Photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-sm uppercase tracking-widest">View</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl"
            onClick={() => setSelectedPhoto(null)}
          >
            ×
          </button>
          <img
            src={`${API}/admin/galleries/public/${galleryId}/media/${selectedPhoto.filename}`}
            alt={selectedPhoto.original_name || "Photo"}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto mt-16 text-center">
        <Card className="bg-card border-border">
          <CardContent className="py-12">
            <h2 className="text-2xl font-serif mb-4">Want galleries like this for your business?</h2>
            <p className="text-muted-foreground mb-6">
              360Lumina helps photographers create stunning galleries, manage bookings, and grow their business.
            </p>
            <Link to="/login">
              <button className="btn-primary px-8 py-3 rounded-sm">
                Get Started Free
              </button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicAdminGalleryDetail;
